import { UnitSystem } from '../../types/googleMaps/googleMaps.types';

const KILOMETERS_PER_MILE = 1.609344;
const SQUARE_KILOMETERS_PER_SQUARE_MILE = KILOMETERS_PER_MILE ** 2;

// Mantissas (1 ≤ m < 10) that read as "round" to a person, e.g. 80, 250, 3 000
const NICE_MANTISSAS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8, 9] as const;

// tolerance against floating point noise, e.g. 2.5 * 10^5 computed as 249999.99999999997
const EPSILON = 1e-9;

/**
 * Rounds a positive number down to the closest "nice" number (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 × 10^n).
 * Rounds down so that a limit presented to the user is never higher than the real one.
 */
export const roundDownToNiceNumber = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return value;
  }

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const mantissa = value / magnitude;
  const niceMantissa = [...NICE_MANTISSAS].reverse().find(nice => nice <= mantissa * (1 + EPSILON)) ?? NICE_MANTISSAS[0];

  return niceMantissa * magnitude;
};

/** Converts a limit given in miles to the unit system; metric results are rounded to a nice number */
export const getNiceDistanceLimit = (miles: number, unitSystem: UnitSystem) => {
  return unitSystem === UnitSystem.metric
    ? roundDownToNiceNumber(miles * KILOMETERS_PER_MILE)
    : miles;
};

/** Converts a limit given in square miles to the unit system; metric results are rounded to a nice number */
export const getNiceAreaLimit = (squareMiles: number, unitSystem: UnitSystem) => {
  return unitSystem === UnitSystem.metric
    ? roundDownToNiceNumber(squareMiles * SQUARE_KILOMETERS_PER_SQUARE_MILE)
    : squareMiles;
};
